/*
Copyright© Celligence International, LLC - 2026

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { useState, useCallback, useEffect, useMemo } from 'react';
import Style from './../../css/BorrowerLoginAudit.module.css';
import {
  Button,
  Collapse,
  IconButton,
  MenuItem,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import { useAuthContext } from '../../contexts/AuthContext';
import { convertToPST } from '../../common/UtilityFunctions';
import useQidList from '../../common/hooks/useQidList';

import useGetBorrowerLoginAudit, { BorrowerLoginAudit, SearchType } from '../../common/hooks/useGetBorrowerLoginAudit';

type AuditTableRowProps = {
  record: BorrowerLoginAudit;
  open: boolean;
  onOpen: () => void;
};

const AuditTableRow = ({ record, open, onOpen }: AuditTableRowProps) => {
  return (
    <>
      <TableRow>
        <TableCell align='center' className={Style.collapseCell}>
          <IconButton aria-label='expand row' size='small' onClick={onOpen}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell align='center'>{convertToPST(record.eventDateTime)}</TableCell>
        <TableCell align='center'>{record.webUserID || '-'}</TableCell>
        <TableCell align='center'>{record.email || '-'}</TableCell>
        <TableCell align='center'>{record.eventTypeDescription || '-'}</TableCell>
        <TableCell align='center'>{record.eventResultDescription || record.eventResult || '-'}</TableCell>
      </TableRow>

      <TableRow>
        <TableCell className={Style.detailCell} colSpan={6}>
          <Collapse in={open} timeout='auto' unmountOnExit className={Style.detailCollapse}>
            <div className={Style.auditDetails}>
              <div className={Style.detailItem}>
                <strong>Login Audit ID:</strong> {record.loginAuditID || '-'}
              </div>
              <div className={Style.detailItem}>
                <strong>Failure Reason:</strong> {record.failureReasonDescription || record.failureReason || '-'}
              </div>
              <div className={Style.detailItem}>
                <strong>IP Address:</strong> {record.IPAddress || '-'}
              </div>
              <div className={Style.detailItem}>
                <strong>IP Version:</strong> {record.IPVersion || '-'}
              </div>
              <div className={Style.detailItem}>
                <strong>Browser:</strong> {record.browserDescription || record.browser || '-'}
              </div>
              <div className={Style.detailItem}>
                <strong>Client OS:</strong> {record.clientOSDescription || '-'}
              </div>
              <div className={Style.detailItem}>
                <strong>Source Application:</strong> {record.sourceAppDescription || record.sourceApp || '-'}
              </div>
            </div>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const BorrowerLoginAuditForm = () => {
  const { loginAuditRecords, errorMessage, loading, clearLoginAuditRecords, getBorrowerLoginAudit } =
    useGetBorrowerLoginAudit();
  const { fullAccessSuperUser } = useAuthContext();
  const { qid: sourceApplicationQID } = useQidList('TBY1_CD.019');

  const [searchType, setSearchType] = useState<SearchType>('sourceApp');
  const [searchValue, setSearchValue] = useState('RCS');

  useEffect(() => {
    if (!fullAccessSuperUser) {
      setSearchType('sourceApp');
      setSearchValue('RCS');
    }
  }, [fullAccessSuperUser]);

  const [eventDate, setEventDate] = useState('');
  const [openRowIds, setOpenRowIds] = useState<Set<string>>(new Set());

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const totalRecords = loginAuditRecords.length;
  const totalPages = useMemo(() => Math.max(1, Math.ceil(totalRecords / rowsPerPage)), [totalRecords, rowsPerPage]);

  const paginatedRecords = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return loginAuditRecords.slice(startIndex, startIndex + rowsPerPage);
  }, [loginAuditRecords, page, rowsPerPage]);

  const firstDisplayedRecord = totalRecords === 0 ? 0 : (page - 1) * rowsPerPage + 1;
  const lastDisplayedRecord = Math.min(page * rowsPerPage, totalRecords);
  const isFirstPage = page === 1;
  const isLastPage = page >= totalPages;

  const handleSearch = () => {
    const normalizedSearchValue = searchValue.trim();
    if (!normalizedSearchValue || !eventDate) return;
    const formattedEventDate = eventDate.replace(/-/g, '');

    setPage(1);
    setOpenRowIds(new Set());
    getBorrowerLoginAudit(searchType, normalizedSearchValue, formattedEventDate);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(Number(event.target.value));
    setPage(1);
    setOpenRowIds(new Set());
  };

  const goToFirstPage = () => {
    setPage(1);
    setOpenRowIds(new Set());
  };

  const goToPreviousPage = () => {
    setPage((currentPage) => Math.max(1, currentPage - 1));
    setOpenRowIds(new Set());
  };

  const goToNextPage = () => {
    setPage((currentPage) => Math.min(totalPages, currentPage + 1));
  };

  const goToLastPage = () => {
    setPage(totalPages);
    setOpenRowIds(new Set());
  };

  const openCollapableRow = useCallback(
    (id: string) => {
      const newSet = new Set(openRowIds);

      if (openRowIds.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }

      setOpenRowIds(newSet);
    },
    [openRowIds],
  );

  return (
    <div className={Style.formContainer}>
      <div className={Style.searchControls}>
        <TextField
          select
          label='Search By'
          value={searchType}
          onChange={(event) => {
            const newSearchType = event.target.value as SearchType;

            setSearchType(newSearchType);
            setSearchValue(newSearchType === 'sourceApp' ? 'RCS' : '');
            setPage(1);
            setOpenRowIds(new Set());
            clearLoginAuditRecords();
          }}
          className={Style.searchType}
          size='small'
        >
          <MenuItem value='webUserID'>Web User ID</MenuItem>
          <MenuItem value='email'>Email</MenuItem>
          <MenuItem value='sourceApp'>Source Application</MenuItem>
        </TextField>

        {searchType === 'sourceApp' ? (
          <TextField
            select
            label='Source Application'
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            disabled={!fullAccessSuperUser}
            className={Style.searchInput}
            size='small'
          >
            {sourceApplicationQID?.map((option, index) => (
              <MenuItem key={index} value={option.code}>
                {option.description}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <TextField
            label={searchType === 'webUserID' ? 'Web User ID' : 'Email'}
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            className={Style.searchInput}
            size='small'
          />
        )}

        <TextField
          label='Event Date'
          type='date'
          value={eventDate}
          onChange={(event) => setEventDate(event.target.value)}
          className={Style.dateInput}
          size='small'
          InputLabelProps={{
            shrink: true,
          }}
        />
        <Button
          variant='contained'
          onClick={handleSearch}
          disabled={loading || !searchValue.trim()}
          className={Style.searchButton}
        >
          {loading ? 'Loading...' : 'Search'}
        </Button>

        {errorMessage && <div>{errorMessage}</div>}

        <div className={Style.tableContainer}>
          <Table stickyHeader aria-label='sticky table' className={Style.auditTable}>
            <TableHead>
              <TableRow>
                <TableCell align='center' className={Style.collapseCell} />
                <TableCell align='center' className={Style.headerCell}>
                  Event Date
                </TableCell>
                <TableCell align='center' className={Style.headerCell}>
                  Web User ID
                </TableCell>
                <TableCell align='center' className={Style.headerCell}>
                  Email
                </TableCell>
                <TableCell align='center' className={Style.headerCell}>
                  Event Type
                </TableCell>
                <TableCell align='center' className={Style.headerCell}>
                  Event Result
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRecords?.map((record: BorrowerLoginAudit) => (
                <AuditTableRow
                  key={record.loginAuditID}
                  record={record}
                  open={openRowIds.has(record.loginAuditID)}
                  onOpen={() => openCollapableRow(record.loginAuditID)}
                />
              ))}
            </TableBody>
          </Table>
          <div className={Style.paginationContainer}>
            <div className={Style.rowsPerPage}>
              <label>
                Rows per page:{' '}
                <select value={rowsPerPage} onChange={handleRowsPerPageChange} aria-label='Rows per page'>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </label>
            </div>

            <div className={Style.recordCount}>
              {firstDisplayedRecord}-{lastDisplayedRecord} of {totalRecords}
            </div>

            <div className={Style.paginationButtons}>
              <Tooltip placement='top' title='First Page'>
                <span>
                  <IconButton
                    onClick={goToFirstPage}
                    disabled={isFirstPage || totalRecords === 0}
                    aria-label='First page'
                    size='small'
                  >
                    <FirstPageIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip placement='top' title='Previous Page'>
                <span>
                  <IconButton
                    onClick={goToPreviousPage}
                    disabled={isFirstPage || totalRecords === 0}
                    aria-label='Previous page'
                    size='small'
                  >
                    <ArrowBackIosNewIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip placement='top' title='Next Page'>
                <span>
                  <IconButton
                    onClick={goToNextPage}
                    disabled={isLastPage || totalRecords === 0}
                    aria-label='Next page'
                    size='small'
                  >
                    <ArrowForwardIosIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip placement='top' title='Last Page'>
                <span>
                  <IconButton
                    onClick={goToLastPage}
                    disabled={isLastPage || totalRecords === 0}
                    aria-label='Last page'
                    size='small'
                  >
                    <LastPageIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BorrowerLoginAuditForm;
